import React from 'react';

import { PanelItem } from '@components/panels/PanelItems';
import { InfoBox } from '@components/cards/messageBox';
import { HorizontalLineSep } from '@components/separators/horizontal_line';
import { ICustomerPotential, SplendInitiative } from '@services/customer/CustomerPotentials';
import { Country } from '@utils/constants/localisation';
import { countrySpecificCurrency, countrySpecificDistanceUnit } from '@utils/misc/functions';
import { CBPAllowedPlans, PlanOptions } from '@utils/planOptions';
import {
    SplendInitiatives,
    EmergencyContactInfo,
    AddressInfo,
    ContractDetails,
    CarInfo,
    PersonalInfo,
    UsedVehicleAddendumDetails,
    DriverDetails,
    CBPOnboard
} from '@context/UserJourneyContext';
import { logger as sentryLogger } from '@sentry/react'
import PriceBreakdownComponent from '@views/QuoteBuilder/PriceBreakdownComponent';


const getInitiativeInfo = (potentialInfo: ICustomerPotential, initiativeInfo: SplendInitiatives): { name: string, discount: string } => {

    let initiative;
    switch (potentialInfo.splend_initiative) {
        case (SplendInitiative.DynamicPricing): {
            initiative = {
                name: potentialInfo.splend_initiative,
                discount: String(initiativeInfo.totalDynamicPricing),
            };
            break;
        }
        default: initiative = { name: '', discount: '' }; break;
    }
    return initiative;
};


const formatAddress = (c: Country, addressAttrs: AddressInfo): string => {
    switch (c) {
        case (Country.GB): { return `${addressAttrs.street} ${addressAttrs.city} ${addressAttrs.postcode}`; }
        case (Country.AU): { return `${addressAttrs.street} ${addressAttrs.city} ${addressAttrs.state} ${addressAttrs.postcode}`; }
        default: console.error(`formatAddress: Unknown country=${c}`); return '';
    }
};


type CustomerProps = {
    firstName: string;
    lastName: string;
    email: string;
};


type DriverLicenceSectionProps = {
    licenceNumber: string;
    state?: string;
    licenceExpiry: string;
    dob: string;
    pcoLicence?: string;
    pcoLicenceExpiry?: string;
};


type EmergencyContactSectionProps = {
    contactName: string;
    phone: string;
    relationship: string;
    address: string;
};


type VehicleDetailsProps = {
    make: string;
    model: string;
    year: number;
    colour: string;
    registration: string;
    vin: string;
};


type ContractDetailsProps = {
    currency: string;
    distUnit: string;
    executionDate: string;
    subsStartDate: string;
    planName: string;
    planType: string;
    excessDistanceCharge: number;
};


const CustomerSection = (props: React.PropsWithChildren<CustomerProps>) => (<>
    <PanelItem header='First name' headerTextStyleCfg={{ bold: true }} value={props.firstName} />
    <PanelItem header='Last name' headerTextStyleCfg={{ bold: true }} value={props.lastName} />
    <PanelItem header='Email' headerTextStyleCfg={{ bold: true }} value={props.email} />
    {props.children}
</>);


const CustomerNationality = ({ nationality }: { nationality: string }) => (
    <PanelItem header='Nationality' headerTextStyleCfg={{ bold: true }} value={nationality} />
);


const CustomerAddress = ({ address }: { address: string }) => (
    <PanelItem header='Address' headerTextStyleCfg={{ bold: true }} value={address} />
);


const CustomerABN = ({ abn }: { abn: string }) => (
    <PanelItem header='Hirer ABN' headerTextStyleCfg={{ bold: true }} value={abn} />
);


const CustomerNINumber = ({ nationalInsuranceNb }: { nationalInsuranceNb: string }) => (
    <PanelItem header='National insurance number' headerTextStyleCfg={{ bold: true }} value={nationalInsuranceNb} />
);


const CustomerExtra = (props: { country: Country, nationality: string, niNumber?: string, abn?: string, address: string }) => (<>
    <CustomerNationality nationality={props.nationality} />
    {props.country == Country.AU
        ? <CustomerABN abn={props.abn || 'missing'} />
        : <CustomerNINumber nationalInsuranceNb={props.niNumber || 'missing'} />}
    <CustomerAddress address={props.address} />
</>);


const DriverLicenceSection = (props: DriverLicenceSectionProps) => (<>
    <PanelItem header='Driver licence number' headerTextStyleCfg={{ bold: true }} value={props.licenceNumber} />
    {props.state && <PanelItem header='State' headerTextStyleCfg={{ bold: true }} value={props.state} />}
    <PanelItem header='Expiry date' headerTextStyleCfg={{ bold: true }} value={props.licenceExpiry} />
    <PanelItem header='Date of birth' headerTextStyleCfg={{ bold: true }} value={props.dob} />
    {!props.state && <PanelItem header='PCO licence number' headerTextStyleCfg={{ bold: true }} value={props.pcoLicence} />}
    {!props.state && <PanelItem header='PCO licence expiry date' headerTextStyleCfg={{ bold: true }} value={props.pcoLicenceExpiry} />}
</>);


const EmergencyContactSection = (props: EmergencyContactSectionProps) => (<>
    <PanelItem header='Emergency contact name' headerTextStyleCfg={{ bold: true }} value={props.contactName} />
    <PanelItem header='Phone' headerTextStyleCfg={{ bold: true }} value={props.phone} />
    <PanelItem header='Relationship to you' headerTextStyleCfg={{ bold: true }} value={props.relationship} />
    <PanelItem header='Address' headerTextStyleCfg={{ bold: true }} value={props.address} />
</>);


const VehicleDetails = (props: VehicleDetailsProps) => (<>
    <PanelItem header='Make' headerTextStyleCfg={{ bold: true }} value={props.make} />
    <PanelItem header='Model' headerTextStyleCfg={{ bold: true }} value={props.model} />
    <PanelItem header='Year' headerTextStyleCfg={{ bold: true }} value={props.year} />
    <PanelItem header='Colour' headerTextStyleCfg={{ bold: true }} value={props.colour} />
    <PanelItem header='Registration plate' headerTextStyleCfg={{ bold: true }} value={props.registration} />
    <PanelItem header='VIN' headerTextStyleCfg={{ bold: true }} value={props.vin} />
</>);


const Odometer = ({ distUnit, distance }: { distUnit: string, distance: number }) => (
    <PanelItem header={`Start ${distUnit}`} headerTextStyleCfg={{ bold: true }} value={distance} />
);


const DistanceAllowance = ({ distUnit, distance }: { distUnit: string, distance: string | number }) => (
    <PanelItem header={`Distance allowance (${distUnit})`} headerTextStyleCfg={{ bold: true }} value={distance} />
);


const ContractDetailsSection = (props: React.PropsWithChildren<ContractDetailsProps>) => (<>
    <PanelItem header='Contract execution date' headerTextStyleCfg={{ bold: true }} value={props.executionDate} />
    <PanelItem header='Subscription start date' headerTextStyleCfg={{ bold: true }} value={props.subsStartDate} />
    <PanelItem header='Plan' headerTextStyleCfg={{ bold: true }} value={props.planName} />
    <PanelItem header='Plan type' headerTextStyleCfg={{ bold: true }} value={props.planType} />
    {props.children}
    <PanelItem
        header={`Excess distance cost per ${props.distUnit} (${props.currency})`}
        headerTextStyleCfg={{ bold: true }}
        value={props.excessDistanceCharge} />
</>);


const SetupFee = ({ currency, value }: { currency: string, value: string }) => (
    <PanelItem header={`Setup fee (${currency})`} headerTextStyleCfg={{ bold: true }} value={value} />
);


const CarInsurance = ({ value }: { value: string }) => (
    <PanelItem header='Comprehensive car insurance' headerTextStyleCfg={{ bold: true }} value={value} />
);


const FOTermLen = ({ planLen }: { planLen: number }) => (
    <PanelItem header='Term length (years)' headerTextStyleCfg={{ bold: true }} value={planLen} />
);


const FOUsedReducedTermWeeks = ({ reducedSubscriptionTerm }: { reducedSubscriptionTerm: number }) => (
    <PanelItem header='Reduced subscription term (weeks)' headerTextStyleCfg={{ bold: true }} value={reducedSubscriptionTerm} />
);


const MinimumPeriod = ({ weeks }: { weeks: string | number }) => (
    <PanelItem header='Minimum period (weeks)' headerTextStyleCfg={{ bold: true }} value={weeks} />
);


const NoticePeriod = ({ days }: { days: number }) => (
    <PanelItem header='Notice period (days)' headerTextStyleCfg={{ bold: true }} value={days} />
);


const InitiativeDetails = (props: React.PropsWithChildren<{ currency: string, weeklyHireFee: number }>) => (<>
    <PanelItem header={`Weekly hire fee (${props.currency})`} headerTextStyleCfg={{ bold: true }} value={props.weeklyHireFee} />
    {props.children}
</>);


const DynamicPricingItem = ({ currency, discount }: { currency: string, discount: string }) => (
    <PanelItem header={`Dynamic pricing weekly discount (${currency})`} headerTextStyleCfg={{ bold: true }} value={discount} />
);


const PriceBeatItems = ({ currency, discount, endDate }: { currency: string, discount: number, endDate: string }) => (<>
    <PanelItem header={`Price beat weekly discount (${currency})`} headerTextStyleCfg={{ bold: true }} value={discount} />
    <PanelItem header='Price beat discount expires' headerTextStyleCfg={{ bold: true }} value={endDate} />
</>);


const shouldDisplayCustomerExtra = (country: Country, plan: string): boolean => {
    switch (country) {
        case (Country.AU): return [
            PlanOptions.SplendFlexiOwn,
            PlanOptions.SplendRentToOwnUsedVehicle,
            PlanOptions.SplendFlexi,
            PlanOptions.RAVShortTermRental
        ].includes(plan as PlanOptions);

        case (Country.GB): return true;

        default: console.error(`Unknown country ${country}!`); return false;
    }
};


const shouldDisplayEmergencyContact = (country: Country, plan: string): boolean => {
    switch (country) {
        case (Country.AU): return [
            PlanOptions.SplendFlexiOwn,
            PlanOptions.SplendRentToOwnUsedVehicle,
            PlanOptions.SplendFlexi,
        ].includes(plan as PlanOptions);

        case (Country.GB): return true;

        default: console.error(`Unknown country ${country}!`); return false;
    }
};


const shouldDisplayFOTermLen = (country: Country, plan: string): boolean => {
    switch (country) {
        case (Country.AU): return [
            PlanOptions.SplendFlexiOwn,
            PlanOptions.SplendRentToOwnUsedVehicle,
        ].includes(plan as PlanOptions);

        case (Country.GB): return [
            PlanOptions.UKFlexiOwn,
            PlanOptions.UKRentToOwnUsed,
        ].includes(plan as PlanOptions);

        default: console.error(`Unknown country ${country}!`); return false;
    }
};


const shouldDisplayFOUsedReducedTerm = (country: Country, plan: string): boolean => {
    switch (country) {
        case (Country.AU): return PlanOptions.SplendRentToOwnUsedVehicle == plan;
        case (Country.GB): return PlanOptions.UKRentToOwnUsed == plan;
        default: console.error(`Unknown country ${country}!`); return false;
    }
};


const InitiativeItems = (props: { country: Country, currency: string, initiative: string, discount: string }) => {
    switch (props.country) {
        case (Country.AU): {
            switch (props.initiative) {
                case (SplendInitiative.DynamicPricing): return <DynamicPricingItem currency={props.currency} discount={props.discount} />;
                default: return null;
            }
        }
        case (Country.GB): {
            switch (props.initiative) {
                case (SplendInitiative.DynamicPricing): return <DynamicPricingItem currency={props.currency} discount={props.discount} />;
                default: return null;
            }
        }
        default: console.error(`InitiativeItems: Unknown country=${props.country}`); return null;
    }
};


const shouldDisplayPriceBeat = (discount: number): boolean => !!discount;


const planIsNotRAV = (country: Country, plan: string): boolean => {
    switch (country) {
        case (Country.AU): {
            return ![
                PlanOptions.RAVCreditHire,
                PlanOptions.RAVFirstPartyHire,
                PlanOptions.RAVFirstPartyHireEV,
                PlanOptions.RAVCreditHireEV,
                PlanOptions.RAVSharecover,
                PlanOptions.RAVShortTermRental,
                PlanOptions.RAVIAG,
            ].includes(plan as PlanOptions);
        }

        case (Country.GB): return true;

        default: console.error(`planNotRav: Unknown country=${country}`); return false;
    }
};

const formatPricingRecord = (cbp: CBPOnboard) => {
    // Formatting CBPOnboard data stored in context to match expected pricingRecord
    // prop used in PriceBreakdownComponent
    return {
        country: cbp.Country,
        pricing_record_sections: cbp.PricingRecordSections,
        pricing_record_line_items: cbp.quoteAddons,
        setup_fee_including_gst: Number(cbp.setupFeeIncludingGst),
    };
}


type OnboardingReviewProps = {
    carInfo: CarInfo,
    addressInfo: AddressInfo,
    contractDetails: ContractDetails,
    driverDetails: DriverDetails,
    personalInfo: PersonalInfo,
    emergencyContactInfo: EmergencyContactInfo,
    usedVehicleAddendum: UsedVehicleAddendumDetails,
    splendInitiatives: SplendInitiatives,
    selectedPotential: ICustomerPotential,
    cbpOnboard: CBPOnboard
};


export const OnboardingReview = (props: OnboardingReviewProps) => {
    sentryLogger.info('OnboardingReview page props:', props);
    const country = props.driverDetails.country;

    // By default, use the contract's distance allowance and minimum period.
    let distanceAllowance: number | string = props.contractDetails.planWeeklyDistAllowance;
    let minimumPeriod: number | string = props.contractDetails.planMinimumPeriod;
    // GB Flexi always uses the plan's minimum term (from Chargebee) and fixed notice period.
    // GB potentials have no minimum period, so the dynamic pricing override below must not apply to it.
    const isGBFlexi = country === Country.GB && props.driverDetails.plan === PlanOptions.UKFlexi;
    if (props.selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
        // The splendInitiative distance allowance/minimum period value has priority over the contract's allowance/minimum period.
        // Only if dynamic pricing onboard. User should have selected these values on the dynamic pricing page.
        distanceAllowance = props.splendInitiatives.weeklyDistanceAllowance || props.contractDetails.planWeeklyDistAllowance;
        if (!isGBFlexi) {
            minimumPeriod = props.splendInitiatives.minimumPeriodWeeks || props.contractDetails.planMinimumPeriod;
        }
        sentryLogger.debug("The selected potential is dynamic pricing, using splendInitiative distance allowance and minimum period values.",
            { distanceAllowance, minimumPeriod });
    }

    const initiative = getInitiativeInfo(props.selectedPotential, props.splendInitiatives);

    return (<>
        <CustomerSection
            firstName={props.personalInfo.firstName}
            lastName={props.personalInfo.lastName}
            email={props.personalInfo.email}
        >
            {shouldDisplayCustomerExtra(country, props.driverDetails.plan) &&
                <CustomerExtra
                    country={country}
                    abn={props.personalInfo.hirerABN}
                    niNumber={props.personalInfo.nationalInsuranceNumber}
                    nationality={props.personalInfo.nationality}
                    address={formatAddress(country, props.addressInfo)}
                />}
        </CustomerSection>

        <HorizontalLineSep />
        <DriverLicenceSection
            licenceNumber={props.driverDetails.driversLicenceNumber}
            state={props.driverDetails.province}
            licenceExpiry={props.driverDetails.driversLicenceExpiry}
            dob={props.driverDetails.dateOfBirth}
            pcoLicence={props.driverDetails.pcoLicenseNumber}
            pcoLicenceExpiry={props.driverDetails.pcoLicenceExpiryDate}
        />

        {shouldDisplayEmergencyContact(country, props.driverDetails.plan) && <>
            <HorizontalLineSep />
            <EmergencyContactSection
                contactName={props.emergencyContactInfo.primaryName}
                phone={props.emergencyContactInfo.primaryPhone}
                relationship={props.emergencyContactInfo.primaryRelationship}
                address={props.emergencyContactInfo.primaryAddress}
            />
        </>}

        <HorizontalLineSep />
        <VehicleDetails
            make={props.carInfo.make}
            model={props.carInfo.model}
            year={props.carInfo.year}
            colour={props.carInfo.colour}
            registration={props.carInfo.registrationPlate}
            vin={props.carInfo.vin}
        />
        <Odometer distUnit={countrySpecificDistanceUnit(country)} distance={props.carInfo.odometer} />

        <HorizontalLineSep />
        <ContractDetailsSection
            currency={countrySpecificCurrency(country)}
            distUnit={countrySpecificDistanceUnit(country)}
            executionDate={props.contractDetails.executionDate}
            subsStartDate={props.contractDetails.subsStartDate}
            planName={props.contractDetails.planName}
            planType={props.driverDetails.plan}
            excessDistanceCharge={props.contractDetails.planAdditionalDistCharge}
        >
            {shouldDisplayFOTermLen(country, props.driverDetails.plan) &&
                <FOTermLen planLen={props.contractDetails.planLen} />}
            {shouldDisplayFOUsedReducedTerm(country, props.driverDetails.plan) &&
                <FOUsedReducedTermWeeks reducedSubscriptionTerm={props.usedVehicleAddendum.reducedSubscriptionTerm} />}
            <DistanceAllowance distUnit={countrySpecificDistanceUnit(country)} distance={distanceAllowance} />
            <MinimumPeriod weeks={minimumPeriod} />
            {isGBFlexi && props.contractDetails.planNoticePeriod != null &&
                <NoticePeriod days={props.contractDetails.planNoticePeriod} />}
        </ContractDetailsSection>
        {planIsNotRAV(country, props.driverDetails.plan) && <>
            <SetupFee currency={countrySpecificCurrency(country)} value={props.contractDetails.planSetupFee || ''} />
            <CarInsurance value={props.contractDetails.insuranceCover} />
        </>}

        <HorizontalLineSep />
        <InitiativeDetails currency={countrySpecificCurrency(country)} weeklyHireFee={props.contractDetails.planWeeklyFee}>
            <InitiativeItems
                country={country}
                currency={countrySpecificCurrency(country)}
                initiative={initiative.name}
                discount={initiative.discount} />
            {shouldDisplayPriceBeat(props.contractDetails.ipbDiscountAmount) &&
                <PriceBeatItems
                    currency={countrySpecificCurrency(country)}
                    discount={props.contractDetails.ipbDiscountAmount}
                    endDate={props.contractDetails.ipbDiscountEndDate} />}
        </InitiativeDetails>
    </>);
};

// `Onboardingv2Review` component is used for both Flexi Own and
// Flexi Onboarding V2.
export const OnboardingV2Review = (props: {
    carInfo: CarInfo,
    addressInfo: AddressInfo,
    contractDetails: ContractDetails,
    driverDetails: DriverDetails,
    personalInfo: PersonalInfo,
    emergencyContactInfo: EmergencyContactInfo,
    cbpOnboard: CBPOnboard
}) => {

    sentryLogger.info('OnboardingFOv2Review page props:', props);
    const country = props.driverDetails.country;

    const distUnit: string = countrySpecificDistanceUnit(country);
    const currencySymbol: string = countrySpecificCurrency(country);

    return (<>
        <PanelItem header='First name' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.firstName} />
        <PanelItem header='Last name' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.lastName} />
        <PanelItem header='Email' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.email} />
        <PanelItem header='Nationality' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.nationality} />
        {country == Country.AU
            ? <PanelItem header='Hirer ABN' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.hirerABN || 'missing'} />
            : <PanelItem header='National insurance number' headerTextStyleCfg={{ bold: true }} value={props.personalInfo.nationalInsuranceNumber || 'missing'} />
        }
        <PanelItem header='Address' headerTextStyleCfg={{ bold: true }} value={formatAddress(country, props.addressInfo)} />

        <HorizontalLineSep />
        <PanelItem header='Driver licence number' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.driversLicenceNumber} />
        {country == Country.AU && <PanelItem header='State' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.province} />}
        <PanelItem header='Expiry date' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.driversLicenceExpiry} />
        <PanelItem header='Date of birth' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.dateOfBirth} />
        {country == Country.GB && <PanelItem header='PCO licence number' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.pcoLicenseNumber} />}
        {country == Country.GB && <PanelItem header='PCO licence expiry date' headerTextStyleCfg={{ bold: true }} value={props.driverDetails.pcoLicenceExpiryDate} />}

        <HorizontalLineSep />
        <PanelItem header='Emergency contact name' headerTextStyleCfg={{ bold: true }} value={props.emergencyContactInfo.primaryName} />
        <PanelItem header='Phone' headerTextStyleCfg={{ bold: true }} value={props.emergencyContactInfo.primaryPhone} />
        <PanelItem header='Relationship to you' headerTextStyleCfg={{ bold: true }} value={props.emergencyContactInfo.primaryRelationship} />
        <PanelItem header='Address' headerTextStyleCfg={{ bold: true }} value={props.emergencyContactInfo.primaryAddress} />

        <HorizontalLineSep />
        <PanelItem header='Make' headerTextStyleCfg={{ bold: true }} value={props.carInfo.make} />
        <PanelItem header='Model' headerTextStyleCfg={{ bold: true }} value={props.carInfo.model} />
        <PanelItem header='Year' headerTextStyleCfg={{ bold: true }} value={props.carInfo.year} />
        <PanelItem header='Colour' headerTextStyleCfg={{ bold: true }} value={props.carInfo.colour} />
        <PanelItem header='Registration plate' headerTextStyleCfg={{ bold: true }} value={props.carInfo.registrationPlate} />
        <PanelItem header='VIN' headerTextStyleCfg={{ bold: true }} value={props.carInfo.vin} />
        <PanelItem header={`Start ${distUnit}`} headerTextStyleCfg={{ bold: true }} value={props.carInfo.odometer} />

        <HorizontalLineSep />
        <PanelItem header='Contract execution date' headerTextStyleCfg={{ bold: true }} value={props.contractDetails.executionDate} />
        <PanelItem header='Subscription start date' headerTextStyleCfg={{ bold: true }} value={props.contractDetails.subsStartDate} />
        <PanelItem header='Plan Name' headerTextStyleCfg={{ bold: true }} value={props.cbpOnboard.PlanId} />
        <PanelItem header='Plan Type' headerTextStyleCfg={{ bold: true }} value={props.cbpOnboard.Plan} />
        {props.cbpOnboard.Plan == CBPAllowedPlans.SplendFlexiOwn &&
            <>
                <PanelItem header='Contract Term (Weeks)' headerTextStyleCfg={{ bold: true }} value={props.cbpOnboard.TermLength * 52} />
                <PanelItem header='Make / Model / Year' headerTextStyleCfg={{ bold: true }} value={`${props.cbpOnboard.Make} / ${props.cbpOnboard.Model} / ${props.cbpOnboard.ModelYear}`} />
            </>
        }
        {props.cbpOnboard.Plan == CBPAllowedPlans.SplendFlexi &&
            <PanelItem header='Minimum Term (Weeks)' headerTextStyleCfg={{ bold: true }} value={props.cbpOnboard.MinimumTerm} />
        }
        <PanelItem
            header={`Excess distance cost per ${distUnit} (${currencySymbol})`}
            headerTextStyleCfg={{ bold: true }}
            value={props.cbpOnboard.ExcessDistanceCost} />
        <DistanceAllowance distUnit={countrySpecificDistanceUnit(country)} distance={props.cbpOnboard.WeeklyDistanceAllowance} />

        <HorizontalLineSep />
        <InfoBox
            testId="InfoBox-GST-Inclusive"
            message={
            <>
                All Prices are listed as <strong>GST Inclusive</strong> where
                applicable.
            </>
            }
        />
        <PriceBreakdownComponent
            pricingRecord={formatPricingRecord(props.cbpOnboard)}
            showTotalWeeklySubscriptionPrice={true}
            showSetupFee={true}
        />
        <div style={{marginBottom: "2.5vh"}}/>
    </>);
};
